import { CoinCard } from "./CoinCard";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Coins } from "lucide-react";

interface Coin {
  id: string;
  name: string;
  ticker: string;
  image?: string | null;
  market_cap: number | null;
  target_market_cap?: number | null;
  progress?: number | null;
  price?: number | null;
  holders?: number | null;
  created_at?: number | null;
  is_locked?: boolean | null;
  is_premium?: boolean | null;
}

interface CoinGridProps {
  coins: Coin[];
  isLoading?: boolean;
}

export function CoinGrid({ coins, isLoading }: CoinGridProps) {
  // Fetch recent trades to calculate price changes
  const { data: priceChanges } = useQuery({
    queryKey: ["price-changes", coins.map(c => c.id)],
    queryFn: async () => {
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
      const coinIds = coins.map(c => c.id);
      
      const { data } = await supabase
        .from("trades")
        .select("coin_id, amount_sol, type")
        .in("coin_id", coinIds)
        .gte("timestamp", oneDayAgo);
      
      // Calculate net buy/sell pressure as price change indicator
      const changes: Record<string, number> = {};
      coinIds.forEach(id => { changes[id] = 0; });
      
      data?.forEach(trade => {
        if (trade.coin_id) {
          const impact = trade.type === "buy" 
            ? (trade.amount_sol ?? 0) * 5 
            : -(trade.amount_sol ?? 0) * 5;
          changes[trade.coin_id] = (changes[trade.coin_id] ?? 0) + impact;
        }
      });
      
      return changes;
    },
    enabled: coins.length > 0,
    staleTime: 30000,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <CoinCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (coins.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
          <Coins className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No coins yet</h3>
        <p className="text-muted-foreground max-w-sm">
          Be the first to launch a coin and start trading on TheMoonRun!
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {coins.map((coin, index) => (
        <div
          key={coin.id}
          className="animate-fade-in"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          <CoinCard
            id={coin.id}
            name={coin.name}
            ticker={coin.ticker}
            image={coin.image}
            marketCap={coin.market_cap ?? 0}
            targetMarketCap={coin.target_market_cap}
            progress={coin.progress}
            price={coin.price}
            holders={coin.holders}
            createdAt={coin.created_at}
            isLocked={coin.is_locked}
            isPremium={coin.is_premium}
            priceChange={priceChanges?.[coin.id] ?? 0}
          />
        </div>
      ))}
    </div>
  );
}

function CoinCardSkeleton() {
  return (
    <div className="p-4 rounded-xl bg-card border border-border/50">
      <div className="flex items-start gap-3 mb-3">
        <Skeleton className="w-12 h-12 rounded-xl" />
        <div className="flex-1">
          <Skeleton className="h-5 w-24 mb-1" />
          <Skeleton className="h-4 w-16" />
        </div>
        <Skeleton className="h-5 w-14" />
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <Skeleton className="h-3 w-16 mb-1" />
          <Skeleton className="h-5 w-20" />
        </div>
        <div>
          <Skeleton className="h-3 w-16 mb-1" />
          <Skeleton className="h-5 w-20" />
        </div>
      </div>
      <Skeleton className="h-2 w-full rounded-full mb-3" />
      <div className="flex justify-between">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-16" />
      </div>
    </div>
  );
}